import { useInfiniteQuery } from 'react-query';
import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';

export type DistributableOrganization = {
  id: string;
  name: string;
  type: 'CUSTOMER' | 'TEST';
};

export type DistributableOrganizationsResponse = {
  content: DistributableOrganization[];
  count: number;
  nextToken?: string;
};

export default function useDistributableOrganizations(
  orgId: string,
  dtbPackageId: string,
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  if (!orgId || !dtbPackageId) {
    return {
      data: undefined,
      isFetching: false,
      isError: false,
      hasNextPage: false,
      fetchNextPage: () => Promise.resolve(),
      refetch: () => Promise.resolve(),
    } as any;
  }

  const baseUrl = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/packages/${dtbPackageId}/distributable-changes`;

  const organizationsQuery = useInfiniteQuery<DistributableOrganizationsResponse, ServerErrorResponse>(
    ['distributableOrganizations', orgId, dtbPackageId],
    async ({ pageParam = '', signal }) => {
      const params = createParamString({
        nextToken: pageParam || '',
      });

      try {
        const data = await fetcher({
          method: 'get',
          url: params ? `${baseUrl}?${params}` : baseUrl,
          options: { signal },
        });

        return data as DistributableOrganizationsResponse;
      } catch (err) {
        onError?.(err as ServerErrorResponse);
        throw err;
      }
    },
    {
      ...options,
      enabled: !!orgId && !!dtbPackageId,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        return lastPage.nextToken || undefined;
      },
    }
  );

  return {
    ...organizationsQuery,
    data: organizationsQuery.data,
  };
}
